<template>
  <el-button
    v-bind="{ ...$attrs, ...defaults }"
    :class="[
      weight && weights[weight],
      textSize && textSizes[textSize],
      roundStyle && rounds[roundStyle],
      {
        'line-height-normal': withIcon,
        'dark-bg': darkBackground,
        'app-animate-button': animated,
        'app-on-hover-animate-button': animatedOnHover,
        [textStyles[textStyle]]: textStyle,
      },
      classes,
      'app-button',
    ]"
  >
    <template
      v-for="(_, scopedSlotName) in $slots"
      #[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>

    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>
  </el-button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type TextWeight =
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'extrabold'
type TextStyle = 'uppercase' | 'lowercase' | 'capitalize' | 'normal-case'
type TextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type RoundType = 'md' | 'none' | 'lg' | 'xs' | 'full' | 'rounded'

const props = withDefaults(
  defineProps<{
    weight?: TextWeight
    withIcon?: boolean
    textStyle?: TextStyle
    bordered?: boolean
    textSize?: TextSize
    roundStyle?: RoundType
    onlyIcon?: boolean
    darkBackground?: boolean
    animatedOnHover?: boolean
    animated?: boolean
  }>(),
  {
    withIcon: false,
    textStyle: 'uppercase',
    textSize: 'md',
    weight: 'bold',
    bordered: false,
    onlyIcon: false,
    animated: false,
    darkBackground: false,
  }
)

const defaults = {}

const weights: Record<TextWeight, string> = {
  light: 'tw-font-light',
  regular: 'tw-font-normal',
  medium: 'tw-font-medium',
  semibold: 'tw-font-semibold',
  bold: 'tw-font-bold',
  extrabold: 'tw-font-extrabold',
}

const rounds: Record<RoundType, string> = {
  xs: 'tw-rounded-xs',
  md: 'tw-rounded-md',
  lg: 'tw-rounded-lg',
  none: 'tw-rounded-none',
  full: 'tw-rounded-full',
  rounded: 'tw-rounded',
}

const textStyles: Record<TextStyle, string> = {
  uppercase: 'tw-uppercase',
  lowercase: 'tw-lowercase',
  capitalize: 'tw-capitalize',
  'normal-case': 'tw-normal-case',
}

const textSizes: Record<TextSize, string> = {
  xs: 'tw-text-xs',
  sm: 'tw-text-sm',
  md: 'tw-text-base',
  lg: 'tw-text-lg',
  xl: 'tw-text-xl',
}

const classes = computed(() => ({
  'tw-border': props.bordered,
  'tw-h-full': props.onlyIcon,
}))
</script>

<style scoped lang="scss">
/* @description
  need to remove issue when zoom out page to 25-50% and button with icon has incorrect alignment with text
*/
.line-height-normal:deep(span) {
  line-height: normal;
  height: inherit;
}

.dark-bg {
  &:hover {
    background-color: $dark-hover !important;
    box-shadow: 0 2px 5px $dark-hover;
  }
}

.app-on-hover-animate-button {
  @apply tw-relative tw-overflow-hidden tw-duration-500;

  &::before {
    content: '';
    @apply tw-absolute;
    top: -5%;
    left: -120%;
    width: 150%;
    height: 110%;

    background: linear-gradient(
      135deg,
      transparent 30%,
      #fff 50%,
      transparent 70%
    );
    transition: transform 0.3s ease-out;
  }

  &:hover {
    box-shadow: 0 4px 30px var(--el-color-primary);

    &::before {
      @apply tw-left-full;
      animation: slide 0.5s ease-in-out forwards;
    }
  }
}

@keyframes slide {
  0% {
    @apply tw--left-full;
  }

  100% {
    @apply tw-left-full;
  }
}

.app-animate-button {
  @apply tw-relative tw-overflow-hidden tw-duration-500;

  &::before {
    content: '';
    @apply tw-absolute;
    top: -5%;
    left: -120%;
    width: 150%;
    height: 110%;

    background: linear-gradient(
      135deg,
      transparent 30%,
      #fff 50%,
      transparent 70%
    );
    transition: transform 0.3s ease-out;
    animation: slide-infinite 2s ease-in-out infinite; /* New infinite animation */
  }

  &:hover {
    box-shadow: 0 4px 30px var(--el-color-primary);

    &::before {
      animation: slide-hover 0.5s ease-in-out forwards;
    }
  }

  // Disable animation if .is-disabled is present
  &.is-disabled,
  .is-disabled & {
    @apply tw-overflow-visible tw-shadow-none;

    &::before {
      content: none; // Removes animation effect
      animation: none;
    }
  }
}

@keyframes slide-hover {
  0% {
    @apply tw--left-full;
  }

  100% {
    @apply tw-left-full;
  }
}

@keyframes slide-infinite {
  0% {
    left: -120%;
  }

  100% {
    left: 120%;
  }
}
</style>
